import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Search, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getEventStatusStyle } from "@/components/EventStatusBadge";
import { getEventReconciliation } from "@/lib/api-financial";
import { cn, formatBRL } from "@/lib/utils";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  panelAccentDot,
  panelPill,
  panelRow,
  panelSectionHeader,
  panelSectionTitle,
  panelSurface,
} from "@/lib/panel-ui";
import { PanelEmpty, panelTableCell, panelTableHead } from "@/components/producer/event-lists/panel-list";

export interface ReconciliationEvent {
  id?: string;
  title: string;
  start_date?: string | null;
  status: string;
  visibility?: string;
  ordersCount?: number;
  ticketsSold?: number;
  ticketsCheckedIn?: number;
  ticketRevenue?: number;
  platformFees?: number;
  gatewayFees?: number;
  refunds?: number;
  netRevenue?: number;
}

interface Props {
  producerId: string;
  events?: ReconciliationEvent[];
  isLoading?: boolean;
}

function ReconciliationStatusBadge({ status, visibility }: { status: string; visibility?: string }) {
  const style = getEventStatusStyle(status, visibility);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-secondary/80",
        style.textColor
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", style.dotColor)} />
      <span>{style.label}</span>
    </span>
  );
}

export default function ProducerEventReconciliation({
  producerId,
  events: externalEvents,
  isLoading: externalLoading,
}: Props) {
  const { activeContext } = useOrganization();
  const { t } = useTranslation("producerSales");
  const [searchQuery, setSearchQuery] = useState("");
  const isOrgContext = activeContext.type === "organization" && activeContext.organizationId;

  const { data: queryEvents = [], isLoading: queryLoading } = useQuery({
    queryKey: ["event-reconciliation", producerId, activeContext],
    queryFn: () => getEventReconciliation(producerId, isOrgContext ? activeContext.organizationId! : undefined),
    staleTime: 30_000,
    enabled: !externalEvents,
  });

  const events: ReconciliationEvent[] = (externalEvents || queryEvents) as ReconciliationEvent[];
  const isLoading = externalLoading ?? queryLoading;

  const displayEvents = useMemo(() => {
    if (!searchQuery.trim()) return events;
    const q = searchQuery.toLowerCase().trim();
    return events.filter((e) => e.title?.toLowerCase().includes(q));
  }, [events, searchQuery]);

  const totalTicketRevenue = useMemo(
    () => displayEvents.reduce((s: number, e) => s + (e.ticketRevenue || 0), 0),
    [displayEvents]
  );
  const totalPlatformFees = useMemo(
    () => displayEvents.reduce((s: number, e) => s + (e.platformFees || 0), 0),
    [displayEvents]
  );
  const totalNet = useMemo(
    () => displayEvents.reduce((s: number, e) => s + (e.netRevenue || 0), 0),
    [displayEvents]
  );
  const totalOrders = useMemo(
    () => displayEvents.reduce((s: number, e) => s + (e.ordersCount || 0), 0),
    [displayEvents]
  );
  const totalTicketsSold = useMemo(
    () => displayEvents.reduce((s: number, e) => s + (e.ticketsSold || 0), 0),
    [displayEvents]
  );
  const totalCheckins = useMemo(
    () => displayEvents.reduce((s: number, e) => s + (e.ticketsCheckedIn || 0), 0),
    [displayEvents]
  );
  const totalGateway = useMemo(
    () => displayEvents.reduce((s: number, e) => s + (e.gatewayFees || 0), 0),
    [displayEvents]
  );
  const totalRefunds = useMemo(
    () => displayEvents.reduce((s: number, e) => s + (e.refunds || 0), 0),
    [displayEvents]
  );

  return (
    <div className={panelSurface}>
      {/* Header da Seção */}
      <div className={cn(panelSectionHeader, "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between")}>
        <div className="flex items-center gap-2.5">
          <span className={panelAccentDot} />
          <h2 className={panelSectionTitle}>{t("reconciliation.sectionTitle", "Conferência por Evento")}</h2>
          <span className="rounded-full bg-secondary/80 px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
            {events.length} {events.length === 1 ? "evento" : "eventos"}
          </span>
        </div>
        {events.length > 3 && (
          <div className="relative w-full sm:w-60">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("reconciliation.searchPlaceholder", "Filtrar por evento...")}
              className="h-8 w-full rounded-full bg-secondary/50 pl-9 pr-3 text-xs border-transparent shadow-none focus-visible:bg-background focus-visible:ring-1 focus-visible:ring-border/80"
            />
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2 p-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-[22px]" />
          ))}
        </div>
      ) : displayEvents.length === 0 ? (
        <div className="p-5">
          <PanelEmpty
            title={searchQuery ? "Nenhum evento corresponde à busca" : t("reconciliation.emptyMessage")}
            hint={searchQuery ? "Tente buscar com outros termos ou limpe o filtro." : undefined}
          />
        </div>
      ) : (
        <>
          {/* Visualização Desktop */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50 bg-secondary/15">
                  <th className={panelTableHead}>{t("reconciliation.event")}</th>
                  <th className={panelTableHead}>{t("reconciliation.status")}</th>
                  <th className={cn(panelTableHead, "text-right")}>{t("reconciliation.tickets")}</th>
                  <th className={cn(panelTableHead, "text-right")}>{t("reconciliation.checkIns")}</th>
                  <th className={cn(panelTableHead, "text-right")}>{t("reconciliation.receivableAmount")}</th>
                  <th className={cn(panelTableHead, "text-right")}>{t("reconciliation.refunds")}</th>
                  <th className={cn(panelTableHead, "text-right")}>{t("reconciliation.netHeader")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {displayEvents.map((evt: ReconciliationEvent) => (
                  <tr key={evt.id || evt.title} className="hover:bg-muted/20 transition-colors group">
                    <td className={panelTableCell}>
                      <Link
                        to={`/produtor/eventos/${evt.id}/painel/financeiro`}
                        className="font-medium text-foreground group-hover:text-primary transition-colors block max-w-[280px] truncate"
                        title={evt.title}
                      >
                        {evt.title}
                      </Link>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {evt.start_date ? format(new Date(evt.start_date), "dd/MM/yyyy") : t("reconciliation.noDate")}
                      </p>
                    </td>
                    <td className={panelTableCell}>
                      <ReconciliationStatusBadge status={evt.status} visibility={evt.visibility} />
                    </td>
                    <td className={cn(panelTableCell, "text-right font-mono text-xs text-foreground/80 tabular-nums")}>
                      {evt.ticketsSold ?? 0}
                    </td>
                    <td className={cn(panelTableCell, "text-right font-mono text-xs text-foreground/80 tabular-nums")}>
                      {evt.ticketsCheckedIn ?? 0}
                    </td>
                    <td className={cn(panelTableCell, "text-right tabular-nums")}>
                      <span className="font-medium text-foreground">{formatBRL(evt.ticketRevenue ?? 0)}</span>
                      <p className="text-[10px] text-muted-foreground">{t("reconciliation.afterPlatformFee")}</p>
                    </td>
                    <td className={cn(panelTableCell, "text-right tabular-nums text-muted-foreground")}>
                      <span>{evt.refunds ? `- ${formatBRL(evt.refunds)}` : "—"}</span>
                    </td>
                    <td className={cn(panelTableCell, "text-right tabular-nums")}>
                      <span
                        className={cn(
                          "font-semibold",
                          (evt.netRevenue || 0) > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"
                        )}
                      >
                        {formatBRL(evt.netRevenue ?? 0)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              {displayEvents.length > 0 && (
                <tfoot>
                  <tr className="border-t border-border/60 bg-secondary/25 font-medium text-xs">
                    <td className="px-4 py-3.5 text-foreground font-semibold">
                      Total ({displayEvents.length})
                    </td>
                    <td className="px-4 py-3.5" />
                    <td className="px-4 py-3.5 text-right font-mono tabular-nums text-foreground">{totalTicketsSold}</td>
                    <td className="px-4 py-3.5 text-right font-mono tabular-nums text-foreground">{totalCheckins}</td>
                    <td className="px-4 py-3.5 text-right tabular-nums font-semibold text-foreground">
                      {formatBRL(totalTicketRevenue)}
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums text-muted-foreground">
                      {totalRefunds > 0 ? `- ${formatBRL(totalRefunds)}` : "—"}
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatBRL(totalNet)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Visualização Mobile (Touch-first com mobile-design) */}
          <div className="p-3 space-y-3 md:hidden">
            {displayEvents.map((evt: ReconciliationEvent) => (
              <div key={evt.id || evt.title} className={cn(panelRow, "p-4 space-y-3.5")}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/produtor/eventos/${evt.id}/painel/financeiro`}
                      className="text-sm font-semibold text-foreground hover:text-primary transition-colors block truncate py-0.5"
                    >
                      {evt.title}
                    </Link>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {evt.start_date ? format(new Date(evt.start_date), "dd/MM/yyyy") : t("reconciliation.noDate")}
                    </p>
                  </div>
                  <ReconciliationStatusBadge status={evt.status} visibility={evt.visibility} />
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1 text-xs">
                  <div className="p-2.5 rounded-2xl bg-secondary/40">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground block font-medium">
                      {t("reconciliation.receivableAmount")}
                    </span>
                    <span className="font-semibold text-foreground tabular-nums text-sm">
                      {formatBRL(evt.ticketRevenue)}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-2xl bg-secondary/40">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground block font-medium">
                      {t("reconciliation.netHeader")}
                    </span>
                    <span
                      className={cn(
                        "font-semibold tabular-nums text-sm",
                        (evt.netRevenue || 0) > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"
                      )}
                    >
                      {formatBRL(evt.netRevenue)}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-2xl bg-secondary/40">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground block font-medium">
                      {t("reconciliation.tickets")}
                    </span>
                    <span className="font-mono text-foreground tabular-nums font-medium">
                      {evt.ticketsSold ?? 0} vendidos
                    </span>
                  </div>
                  <div className="p-2.5 rounded-2xl bg-secondary/40">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground block font-medium">
                      {t("reconciliation.checkIns")}
                    </span>
                    <span className="font-mono text-foreground tabular-nums font-medium">
                      {evt.ticketsCheckedIn ?? 0} realizados
                    </span>
                  </div>
                </div>

                <div className="pt-1 flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    asChild
                    className={cn("min-h-[44px] px-4 rounded-full text-xs font-medium text-muted-foreground hover:text-foreground active:scale-[0.97]", panelPill)}
                  >
                    <Link to={`/produtor/eventos/${evt.id}/painel/financeiro`}>
                      <span>Ver financeiro</span>
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
